import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../../utils/analytics';

/* ── Palette (from the homepage-hero handoff) ────────────────────────────── */
const BG   = '#09060F';
const E    = '#10B981';   // emerald — RevSec / primary accent
const P     = '#7C3AED';  // purple — RevUpAI brand / primary CTA
const LAV  = '#EDE9F8';
const SUB  = '#DDD8F2';
const BODY = '#9B90BA';
const SOFT = '#C8C0EC';

const ROLES = ['CISO', 'DBA', 'Architect', 'QA lead', 'SOC analyst', 'data engineer', '— any role'];

/* Operated roster rows */
const ROSTER = [
  {
    name: 'RevSec', cat: 'AI Security & Governance', route: '/ai-ciso',
    iconStroke: '#10B981', iconBg: 'rgba(16,185,129,.12)', iconBorder: 'rgba(16,185,129,.3)',
    chipColor: '#6EE7B7', chipBg: 'rgba(16,185,129,.12)', chipBorder: 'rgba(16,185,129,.3)',
    hover: 'rgba(255,255,255,.03)',
    icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></>,
  },
  {
    name: 'RevMigrate', cat: 'Data Migration & Modernization', route: '/ai-dba',
    iconStroke: '#F59E0B', iconBg: 'rgba(245,158,11,.1)', iconBorder: 'rgba(245,158,11,.28)',
    chipColor: '#FCD34D', chipBg: 'rgba(245,158,11,.12)', chipBorder: 'rgba(245,158,11,.3)',
    hover: 'rgba(255,255,255,.03)',
    icon: <><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></>,
  },
  {
    name: 'RevModernize', cat: 'AI Architecture', route: '/ai-architect',
    iconStroke: '#A78BFA', iconBg: 'rgba(139,92,246,.12)', iconBorder: 'rgba(139,92,246,.3)',
    chipColor: '#C4B5FD', chipBg: 'rgba(139,92,246,.14)', chipBorder: 'rgba(139,92,246,.32)',
    hover: 'rgba(255,255,255,.03)',
    icon: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>,
  },
  {
    name: 'RevTest', cat: 'AI QA & Testing', route: null,
    iconStroke: '#22D3EE', iconBg: 'rgba(34,211,238,.1)', iconBorder: 'rgba(34,211,238,.28)',
    chipColor: '#7DD3FC', chipBg: 'rgba(34,211,238,.1)', chipBorder: 'rgba(34,211,238,.28)',
    hover: 'rgba(255,255,255,.03)',
    icon: <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  },
];

const STATS = [
  { value: '89%', color: '#10B981', label: 'of shadow AI surfaced within 48 hours' },
  { value: '$1.88M', color: '#F59E0B', label: 'average annual saving per migration' },
  { value: '97%', color: '#A78BFA', label: 'policy compliance achieved' },
];

export default function HeroOperated() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);

  useEffect(() => {
    const m = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    if (m && m.matches) return; // reduced motion → freeze on first word
    const t = setInterval(() => setI(n => n + 1), 1700);
    return () => clearInterval(t);
  }, []);

  const roleWord = ROLES[i % ROLES.length];
  const go = () => { window.scrollTo(0, 0); navigate('/contact'); };
  const goTo = (path) => { window.scrollTo(0, 0); navigate(path); };
  const seeRoles = () => {
    const el = document.getElementById('meet-agents');
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 64, behavior: 'smooth' });
  };

  return (
    <section style={{ position: 'relative', overflow: 'hidden', background: BG, padding: 'clamp(88px,9vh,108px) clamp(20px,5vw,64px) clamp(36px,4vh,52px)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes h2caret { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes h2blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        @media (max-width:980px){
          .hero2-grid { grid-template-columns:1fr !important; gap:44px !important; }
          .proof2-frameworks { margin-left:0 !important; margin-top:18px; }
        }
      `}</style>

      {/* ambient background */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '-16%', left: '-8%', width: 680, height: 680, borderRadius: '50%', background: 'radial-gradient(circle,rgba(16,185,129,.12) 0%,transparent 65%)', filter: 'blur(85px)' }} />
        <div style={{ position: 'absolute', bottom: '-22%', right: '-6%', width: 560, height: 560, borderRadius: '50%', background: 'radial-gradient(circle,rgba(124,58,237,.16) 0%,transparent 65%)', filter: 'blur(78px)' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,.014) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.014) 1px,transparent 1px)', backgroundSize: '82px 82px', WebkitMaskImage: 'radial-gradient(ellipse 72% 62% at 36% 44%,black 0%,transparent 100%)', maskImage: 'radial-gradient(ellipse 72% 62% at 36% 44%,black 0%,transparent 100%)' }} />
      </div>

      {/* hero grid */}
      <div className="hero2-grid" style={{ position: 'relative', zIndex: 2, flex: 1, width: '100%', maxWidth: 1440, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.02fr .98fr', gap: 'clamp(40px,5vw,72px)', alignItems: 'center' }}>

        {/* LEFT */}
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '7px 16px', borderRadius: 100, marginBottom: 28, background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.22)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: E, animation: 'h2blink 2.2s infinite' }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: E }}>Operated AI · any role your enterprise needs</span>
          </div>

          <h1 style={{ fontFamily: "'Fraunces',Georgia,serif", fontWeight: 800, lineHeight: 1.04, letterSpacing: '-2px', fontSize: 'clamp(40px,4.6vw,64px)', color: LAV, margin: '0 0 24px' }}>
            Meet your operated AI <span style={{ color: E }}>{roleWord}</span>
            <span style={{ display: 'inline-block', width: 4, height: '.78em', background: E, marginLeft: 5, verticalAlign: '-.12em', animation: 'h2caret 1s step-end infinite', borderRadius: 2 }} />
          </h1>

          <p style={{ fontSize: 'clamp(15px,1.2vw,18px)', fontWeight: 300, lineHeight: 1.62, color: BODY, maxWidth: 540, margin: '0 0 16px' }}>
            From <strong style={{ color: SUB, fontWeight: 600 }}>AI security &amp; governance</strong> to data migration, architecture, and QA — proven roles operating in enterprises today. Need one we don't offer yet? We build it on the same platform and operate it with our experts.
          </p>
          <p style={{ fontSize: 'clamp(15px,1.05vw,16.5px)', fontWeight: 600, lineHeight: 1.5, color: E, margin: '0 0 34px' }}>
            Proven roles, ready now — plus any role you need.
          </p>

          <div style={{ display: 'flex', gap: 13, flexWrap: 'wrap' }}>
            <button onClick={() => { track('book_demo', { location: 'hero' }); go(); }}
              style={{ padding: '15px 30px', borderRadius: 11, cursor: 'pointer', background: P, color: '#fff', border: 'none', fontSize: 15, fontWeight: 700, fontFamily: "'DM Sans',system-ui,sans-serif", boxShadow: '0 8px 30px rgba(124,58,237,.42)', transition: 'all .2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 40px rgba(124,58,237,.6)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(124,58,237,.42)'; }}
            >Book a Discovery Call →</button>
            <button onClick={seeRoles}
              style={{ padding: '15px 26px', borderRadius: 11, cursor: 'pointer', background: 'transparent', color: '#A7F3D0', border: '1px solid rgba(16,185,129,.4)', fontSize: 15, fontWeight: 600, fontFamily: "'DM Sans',system-ui,sans-serif", transition: 'all .2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,.08)'; e.currentTarget.style.borderColor = 'rgba(16,185,129,.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(16,185,129,.4)'; }}
            >See the roles ↓</button>
          </div>
        </div>

        {/* RIGHT: operated roster */}
        <div style={{ background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,.45)' }}>
          <div style={{ padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,.03)', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(200,192,236,.55)' }}>Working in enterprises now</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, color: E }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: E, animation: 'h2blink 2s infinite' }} />4 operating</span>
          </div>

          <div style={{ padding: '6px 10px' }}>
            {ROSTER.map((r, idx) => (
              <React.Fragment key={r.name}>
                {idx > 0 && <div style={{ height: 1, background: 'rgba(255,255,255,.05)', margin: '0 13px' }} />}
                <div onClick={() => r.route && goTo(r.route)} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 13, borderRadius: 11, cursor: r.route ? 'pointer' : 'default', transition: 'background .2s ease' }}
                  onMouseEnter={e => e.currentTarget.style.background = r.hover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 11, background: r.iconBg, border: `1px solid ${r.iconBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={r.iconStroke} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{r.icon}</svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14.5, fontWeight: 700, color: LAV, margin: 0 }}>{r.name}</p>
                    <p style={{ fontSize: 11.5, color: 'rgba(200,192,236,.55)', margin: '2px 0 0' }}>{r.cat}</p>
                  </div>
                  <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: .5, color: r.chipColor, background: r.chipBg, border: `1px solid ${r.chipBorder}`, borderRadius: 100, padding: '4px 10px', flexShrink: 0 }}>● OPERATING</span>
                </div>
              </React.Fragment>
            ))}

            <div style={{ height: 1, background: 'rgba(255,255,255,.05)', margin: '0 13px' }} />
            <div onClick={go} style={{ display: 'flex', alignItems: 'center', gap: 13, padding: 13, borderRadius: 11, cursor: 'pointer', transition: 'background .2s ease' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,.03)', border: '1px dashed rgba(16,185,129,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: E, fontSize: 20, fontWeight: 300 }}>+</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14.5, fontWeight: 700, color: '#A7F3D0', margin: 0 }}>A custom role</p>
                <p style={{ fontSize: 11.5, color: 'rgba(200,192,236,.55)', margin: '2px 0 0' }}>built &amp; operated on the same platform</p>
              </div>
              <span style={{ color: E, fontSize: 18, flexShrink: 0 }}>→</span>
            </div>
          </div>

          <div style={{ padding: '13px 22px', background: 'rgba(124,58,237,.06)', borderTop: '1px solid rgba(124,58,237,.16)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
            <span style={{ fontSize: 12, fontWeight: 500, color: SOFT }}>Operated by our experts, with your team in command.</span>
          </div>
        </div>
      </div>

      {/* proof band */}
      <div className="proof2-band" style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 1440, margin: 'clamp(32px,4vh,52px) auto 0', paddingTop: 'clamp(24px,3vh,34px)', borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'clamp(28px,4vw,56px)', flexWrap: 'wrap' }}>
          {STATS.map(s => (
            <div key={s.value}>
              <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 'clamp(26px,2.4vw,34px)', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <p style={{ fontSize: 12, color: 'rgba(200,192,236,.55)', margin: '7px 0 0', maxWidth: 150, lineHeight: 1.4 }}>{s.label}</p>
            </div>
          ))}
        </div>
        <div className="proof2-frameworks" style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: 9 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(200,192,236,.4)' }}>Operated against your frameworks</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['NIST AI RMF', 'EU AI Act', 'ISO 42001', 'SOC 2'].map(f => (
              <span key={f} style={{ fontSize: 12, fontWeight: 600, color: SOFT, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '6px 12px' }}>{f}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
